import { useEffect, useMemo, useState } from 'react';

import { Pagination } from 'components';
import { debounce } from 'lodash';

interface UsePaginationParams {
  defaultPageSize: number;
  shouldUpdateUrl?: boolean;
}

function usePagination({ defaultPageSize, shouldUpdateUrl = false }: UsePaginationParams) {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalDataCount, setTotalDataCount] = useState(0);

  const getPaginationParamsFromUrl = () => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    return {
      page: params.get('page'),
      size: params.get('size'),
    };
  };

  useEffect(() => {
    const updatePaginationState = () => {
      const { page, size } = getPaginationParamsFromUrl();
      setPageNumber(page ? Number(page) : 1);
      if (size) setPageSize(Number(size));
    };

    updatePaginationState();
    window.addEventListener('popstate', updatePaginationState);

    return () => {
      window.removeEventListener('popstate', updatePaginationState);
    };
  }, []);

  const updateUrlParams = useMemo(
    () =>
      debounce((newPage: number, newSize: number) => {
        if (shouldUpdateUrl) {
          const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
          const currentPage = params.get('page');
          const currentSize = params.get('size');
          const hashPath = window.location.hash.split('?')[0];

          if (newPage > 1) {
            if (currentPage !== newPage.toString() || currentSize !== newSize.toString()) {
              params.set('page', newPage.toString());
              params.set('size', newSize.toString());
              window.history.pushState(
                { page: newPage, size: newSize },
                '',
                `${hashPath}?${params}`
              );
            }
          } else {
            if (currentPage || currentSize) {
              params.delete('page');
              params.delete('size');
              const newUrl = params.toString() ? `${hashPath}?${params}` : hashPath;
              window.history.pushState({}, '', newUrl);
            }
          }
        }
      }, 300),
    [shouldUpdateUrl]
  );

  useEffect(() => {
    updateUrlParams(pageNumber, pageSize);
  }, [pageNumber, pageSize, updateUrlParams]);

  const handleSetPageSize = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const PageController = useMemo(
    () => (extraProps: { onDownload?: () => void }) =>
      (
        <Pagination
          totalCount={totalDataCount}
          pageNumber={pageNumber}
          setPageSize={handleSetPageSize}
          defaultSize={pageSize}
          setPageNumber={setPageNumber}
          {...extraProps}
        />
      ),
    [pageNumber, totalDataCount, pageSize]
  );

  return {
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize: handleSetPageSize,
    totalDataCount,
    setTotalDataCount,
    PageController,
  };
}

export default usePagination;
